// Folder Management
export class FolderManager {
  constructor(stateManager, photoManager, uiManager, modalManager) {
    this.stateManager = stateManager;
    this.photoManager = photoManager;
    this.uiManager = uiManager;
    this.modalManager = modalManager;
    this.editorState = stateManager.getEditorState();
    this.folderStateManager = stateManager.getFolderStateManager();
  }

  /**
   * Load all folders
   */
  async loadFolders() {
    try {
      console.log('Loading folders from /api/v1/folders...');
      const response = await fetch('/api/v1/folders');
      console.log('Folders API response status:', response.status);
      
      const folders = await response.json();
      console.log('Folders loaded:', folders);
      console.log('Number of folders:', folders.length);
      
      // Create FolderState for each folder (with minimal photo data)
      folders.forEach(folder => {
        this.folderStateManager.createFolderState(folder);
      });
      
      return folders;
    } catch (error) {
      console.error('Error loading folders:', error);
      return [];
    }
  }

  /**
   * Get current folders
   */
  getFolders() {
    return this.folderStateManager.getAllFolders();
  }
  
  /**
   * Get FolderState by ID
   */
  getFolderState(folderId) {
    return this.folderStateManager.getFolderState(folderId);
  }

  /**
   * Select a folder
   */
  async selectFolder(folderId) {
    const folderState = this.folderStateManager.getFolderState(folderId);
    if (!folderState) {
      console.error('FolderState not found for folder', folderId);
      return;
    }
    
    // Update reactive state
    this.editorState.batch(() => {
      this.editorState.set({ currentFolderId: folderId });
      this.editorState.clearPhotoSelection();
      this.editorState.set({ 
        activePhotoId: null
      });
    });
    
    // Update PhotoManager's current folder
    this.photoManager.currentFolderId = folderId;
    
    // Load basic photo metadata if not already loaded
    if (!folderState.hasMetadata()) {
      const photoMetadata = await this.stateManager.photoService.getBasicInfo(folderId);
      folderState.setPhotoMetadata(photoMetadata);
    }
    
    const photoMetadata = folderState.getPhotoMetadata();
    
    // Render photos with metadata IMMEDIATELY (don't wait for active photo to load)
    // This shows thumbnails right away without blocking
    const updatedState = this.editorState.get();
    this.uiManager.renderPhotos(photoMetadata, updatedState.selectedPhotoIds, null);
    
    // Start polling if needed
    folderState.startPollingIfNeeded();
    
    // Try to restore the active photo for this folder from localStorage (async, non-blocking)
    const savedPhotoId = this.stateManager.loadActivePhotoFromLocalStorage(folderId);
    let activePhoto = null;
    
    if (savedPhotoId) {
      // Check if the saved photo exists in metadata
      const savedPhotoMetadata = photoMetadata.find(p => p.id === savedPhotoId);
      if (savedPhotoMetadata) {
        // Load full data for the saved photo asynchronously (don't block render)
        this.stateManager.photoService.getFullInfo([savedPhotoId]).then(photos => {
          if (photos.length > 0) {
            activePhoto = photos[0];
            this.editorState.setActivePhoto(activePhoto.id, activePhoto);
            
            // Re-render to show active photo state
            const state = this.editorState.get();
            this.uiManager.renderPhotos(photoMetadata, state.selectedPhotoIds, activePhoto);
          }
        });
      }
    }
    
    // If we're on edit page, show a photo
    if (updatedState.currentPage === 'edit' && photoMetadata.length > 0) {
      if (savedPhotoId) {
        // If we have a saved photo, load it for edit page
        // (the async load above will handle Photos page, but Edit page needs it now)
        const savedPhotoMetadata = photoMetadata.find(p => p.id === savedPhotoId);
        if (savedPhotoMetadata) {
          const photos = await this.stateManager.photoService.getFullInfo([savedPhotoId]);
          if (photos.length > 0) {
            this.photoManager.editPhoto(photos[0]);
          }
        } else {
          // Saved photo doesn't exist, load first photo
          const firstPhotos = await this.stateManager.photoService.getFullInfo([photoMetadata[0].id]);
          if (firstPhotos.length > 0) {
            this.photoManager.editPhoto(firstPhotos[0]);
          }
        }
      } else {
        // No saved photo, load first photo's full data
        const firstPhotos = await this.stateManager.photoService.getFullInfo([photoMetadata[0].id]);
        if (firstPhotos.length > 0) {
          this.photoManager.editPhoto(firstPhotos[0]);
        }
      }
    }
  }

  /**
   * Add a new folder (either granted or cloud)
   */
  async addFolder() {
    // Check if File System Access API is available
    if (window.showDirectoryPicker) {
      try {
        const dirHandle = await window.showDirectoryPicker({
          mode: 'readwrite'
        });
        await this.createGrantedFolder(dirHandle);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error selecting directory:', error);
        }
      }
    } else {
      // Fallback to cloud folder
      this.modalManager.show('folder-name-modal');
      document.getElementById('folder-name-input')?.focus();
    }
  }

  /**
   * Create a granted folder (with file system access)
   */
  async createGrantedFolder(dirHandle) {
    const guid = crypto.randomUUID();
    
    // Save file handle to IndexedDB
    await this.stateManager.saveFileHandle(guid, dirHandle);
    
    // Create folder in backend
    const response = await fetch('/api/v1/folders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        folder: {
          name: dirHandle.name,
          full_path: dirHandle.name,
          folder_type: 0, // granted
          file_handle_guid: guid
        }
      })
    });

    const folder = await response.json();
    
    // Create FolderState for the new folder
    this.folderStateManager.createFolderState(folder);
    
    // Select the new folder
    await this.selectFolder(folder.id);

    // Upload files from directory
    await this.uploadFilesFromDirectory(dirHandle, folder.id);
  }

  /**
   * Create a cloud folder (manual upload)
   */
  async createCloudFolder(name) {
    const response = await fetch('/api/v1/folders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        folder: {
          name: name,
          folder_type: 1 // cloud
        }
      })
    });

    const folder = await response.json();
    
    // Create FolderState for the new folder
    this.folderStateManager.createFolderState(folder);
    
    // Select the new folder
    await this.selectFolder(folder.id);
    
    return folder;
  }

  /**
   * Upload files from a directory handle
   */
  async uploadFilesFromDirectory(dirHandle, folderId) {
    const files = [];
    for await (const entry of dirHandle.values()) {
      if (entry.kind === 'file') {
        const file = await entry.getFile();
        if (file.type.startsWith('image/')) {
          files.push(file);
        }
      }
    }

    // Sort files lexically by name
    files.sort((a, b) => a.name.localeCompare(b.name));

    await this.uploadFiles(files, folderId);
  }

  /**
   * Upload files to a folder with progress tracking
   */
  async uploadFiles(files, folderId) {
    const folderState = this.folderStateManager.getFolderState(folderId);
    if (!folderState) {
      console.error('FolderState not found for folder', folderId);
      return;
    }
    
    // Sort files lexically by name before uploading
    const sortedFiles = Array.from(files).sort((a, b) => a.name.localeCompare(b.name));
    
    // Start upload progress in FolderState
    folderState.startUpload(sortedFiles.length);

    // Upload with progress callback that also updates photos in real-time
    await this.photoManager.uploadFiles(
      sortedFiles, 
      folderId, 
      async (uploaded, total, uploadedPhoto) => {
        folderState.incrementUploadProgress();
        
        // If a photo was just uploaded, add it to FolderState
        if (uploadedPhoto) {
          folderState.addPhoto(uploadedPhoto);
          
          // If this is the currently selected folder, update the photo grid
          const state = this.editorState.get();
          if (state.currentFolderId === folderId) {
            const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
            this.uiManager.renderPhotos(folderState.getPhotos(), state.selectedPhotoIds, activePhoto);
          }
        }
      },
      () => {
        // Called when ALL uploads (all sizes for all photos) complete
        console.log(`[FolderManager] All uploads complete (all sizes for ${sortedFiles.length} photos), calling completeUpload()`);
        folderState.completeUpload();
      },
      (totalUploaded, totalCount, currentSizeName, currentSizeUploaded, currentSizeTotal) => {
        // Called on each upload completion with detailed progress
        folderState.updateUploadProgress(
          totalUploaded, 
          totalCount, 
          currentSizeName, 
          currentSizeUploaded, 
          currentSizeTotal
        );
      }
    );
  }

  /**
   * Handle dropped files
   */
  async handleDroppedFiles(files) {
    // Create a new cloud folder with random name
    const folderName = `Dropped ${new Date().toISOString().split('T')[0]}`;
    const folder = await this.createCloudFolder(folderName);
    await this.uploadFiles(files, folder.id);
  }

  /**
   * Delete a folder
   */
  async deleteFolder(folder) {
    const confirmed = await this.modalManager.confirm(
      `Delete folder "${folder.name}" and all its photos?`
    );
    
    if (!confirmed) return;

    try {
      const response = await fetch(`/api/v1/folders/${folder.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Remove FolderState
        this.folderStateManager.removeFolderState(folder.id);
        
        const state = this.editorState.get();
        if (state.currentFolderId === folder.id) {
          // If we deleted the current folder, try to select another one
          const folders = this.folderStateManager.getAllFolders();
          const nextFolder = folders.find(f => f.id !== folder.id);
          
          if (nextFolder) {
            // Select another folder
            await this.selectFolder(nextFolder.id);
          } else {
            // No folders left, clear state
            this.editorState.set({ currentFolderId: null });
            this.photoManager.photos = [];
            this.uiManager.renderPhotos([], state.selectedPhotoIds, null);
          }
        }
        
        this.stateManager.saveState();
      } else {
        // Try to get error details from response
        let errorMessage = 'Failed to delete folder';
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
            if (errorData.details) {
              errorMessage += ': ' + (Array.isArray(errorData.details) ? errorData.details.join(', ') : errorData.details);
            }
          }
        } catch (e) {
          // If JSON parsing fails, use default message
        }
        console.error('Failed to delete folder:', errorMessage);
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert('Failed to delete folder: ' + error.message);
    }
  }

  /**
   * Show folder context menu
   */
  showFolderContextMenu(folder, x, y) {
    this.modalManager.showContextMenu([
      {
        label: 'Delete',
        danger: true,
        icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>',
        action: () => this.deleteFolder(folder)
      }
    ], x, y);
  }
}

